import {
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { EMPTY } from 'rxjs';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { DialogRef, DIALOG_DATA } from './dialog-ref';
import { EnsembleDialogComponent } from './ensemble-dialog.component';

export interface DialogConfig<TData = unknown> {
  /** Data injected into the content component via `DIALOG_DATA`. */
  data?: TData;
  /** Text shown in the modal header (maps to `<en-modal label="...">` ). */
  label?: string;
  /** When true the modal does not close on backdrop click (`persist` attribute). */
  persist?: boolean;
  /** Optional CSS class applied directly to the `<en-modal>` shell. */
  modalClass?: string;
}

/**
 * Service for opening dialogs backed by Ensemble's `<en-modal>` web component.
 *
 * Creates a fresh `EnsembleDialogComponent` shell for each call, appends it to
 * `document.body`, and dynamically renders the provided content component inside
 * it.  The shell is destroyed automatically when the returned `DialogRef` closes.
 *
 * ### Opening a dialog
 * ```typescript
 * const ref = this.dialog.open(MyContentComponent, {
 *   label: 'My Title',
 *   data: { id: 42 },
 * });
 *
 * ref.afterClosed$.subscribe(result => console.log(result));
 * ```
 *
 * ### Content component contract
 * ```typescript
 * @Component({ ... })
 * export class MyContentComponent {
 *   data = inject<{ id: number }>(DIALOG_DATA);
 *   dialogRef = inject<DialogRef<string>>(DialogRef);
 *
 *   confirm() { this.dialogRef.close('confirmed'); }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class EnsembleDialogService {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);

  /**
   * Opens `component` inside an Ensemble modal shell.
   *
   * @param component  The Angular component to render as dialog content.
   * @param config     Optional label, data payload, and persist flag.
   * @returns          A `DialogRef` that can be used to close the dialog or react to closure.
   */
  open<TData = unknown, TResult = unknown>(
    component: Type<unknown>,
    config?: DialogConfig<TData>,
  ): DialogRef<TResult> {
    const dialogRef = new DialogRef<TResult>();

    // ── 1. Create the shell ────────────────────────────────────────────────
    const shellRef = createComponent(EnsembleDialogComponent, {
      environmentInjector: this.envInjector,
    });

    const dataTitle = (config?.data as { title?: string } | undefined)?.title;
    shellRef.instance.label   = dataTitle ?? '';
    shellRef.instance.persist = config?.persist ?? false;
    shellRef.instance.modalClass = config?.modalClass ?? '';

    // ── 2. Attach to app + DOM ─────────────────────────────────────────────
    this.appRef.attachView(shellRef.hostView);
    document.body.appendChild(shellRef.location.nativeElement);

    // Trigger lifecycle so ViewChild queries (contentHost) are resolved.
    shellRef.changeDetectorRef.detectChanges();

    // ── 3. Render the content component inside the shell ───────────────────
    // Compat adapter: legacy content components inject MatLegacyDialogRef and
    // MAT_LEGACY_DIALOG_DATA — bridge them to our DialogRef and data so they
    // continue to work without requiring any changes to the content components.
    const matDialogRefCompat = {
      close: (result?: unknown) => dialogRef.close(result as TResult),
      afterClosed: () => dialogRef.afterClosed$,
      beforeClosed: () => EMPTY,
      backdropClick: () => EMPTY,
      keydownEvents: () => EMPTY,
      disableClose: false,
    };

    const contentInjector = Injector.create({
      providers: [
        { provide: DIALOG_DATA,   useValue: config?.data      },
        { provide: DialogRef,     useValue: dialogRef         },
        { provide: MAT_DIALOG_DATA, useValue: config?.data    },
        { provide: MatDialogRef,  useValue: matDialogRefCompat },
      ],
      parent: this.envInjector,
    });

    // ── 4. Teardown — registered BEFORE content creation ──────────────────
    // afterClosed$ is a plain Subject: it does not replay. If a content
    // component calls dialogRef.close() synchronously during construction or
    // initial change-detection, the emission would be missed by a subscription
    // registered after createComponent(). Registering here guarantees the shell
    // is always destroyed, regardless of when close() is called.
    let isClosed = false;

    dialogRef.afterClosed$.subscribe(() => {
      isClosed = true;
      shellRef.instance.hide();
      queueMicrotask(() => {
        this.appRef.detachView(shellRef.hostView);
        shellRef.destroy();
      });
    });

    const contentRef = shellRef.instance.contentHost.createComponent(component, {
      injector: contentInjector,
      environmentInjector: this.envInjector,
    });

    // Explicitly run CD on the content component itself — needed for module-based
    // OnPush components whose first render may not propagate from the parent.
    contentRef.changeDetectorRef.detectChanges();

    // Sync the shell's view tree.
    shellRef.changeDetectorRef.detectChanges();

    // ── 3b. Hoist slotted elements to be direct children of <en-modal> ────────
    // Shadow DOM slot distribution requires slotted elements to be *direct*
    // light-DOM children of the host element (<en-modal>). Angular wraps the
    // content component in its own host element, making slot="*" divs grandchildren
    // instead of direct children — so they never get distributed.
    // The isClosed guard prevents operating on an already-destroyed shell if
    // close() was called synchronously before this microtask runs.
    queueMicrotask(() => {
      if (isClosed) return;
      const enModal = shellRef.location.nativeElement.querySelector('en-modal');
      const contentEl = contentRef.location.nativeElement as HTMLElement;
      if (enModal && contentEl) {
        contentEl.querySelectorAll<HTMLElement>('[slot]').forEach(slotEl => {
          enModal.appendChild(slotEl);
        });
      }
    });


    return dialogRef;
  }
}




